/* eslint-disable replo/no-tailwind-literal-color */
/* eslint-disable replo/no-literal-color-in-templates */
import * as React from "react";

import {
  BsChevronRight,
  BsImage,
  BsInfoCircle,
  BsStarFill,
  BsStarHalf,
} from "react-icons/bs";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

interface RenderComponentPlaceholderProps {
  title?: string | null;
  descriptionOverride?: string | null;
  icon?: () => React.ReactNode;
  style?: React.CSSProperties;
  sectionName?: string;
  type?:
    | "productOptions"
    | "reviewStars"
    | "reviewFull"
    | "gallery"
    | "form"
    | "shopifySection"
    | "html";
}

const PlaceholderAlert: React.FC<RenderComponentPlaceholderProps> = ({
  title,
  descriptionOverride,
  type,
  sectionName,
}) => {
  let detail: string;
  if (!type) {
    detail = "view the full component.";
  } else {
    detail = exhaustiveSwitch({ type })({
      html: () => "see your custom code in action.",
      productOptions: () => "view the full component.",
      reviewStars: () => "view the star rating.",
      reviewFull: () => "view the full reviews.",
      gallery: () => "view the full component.",
      shopifySection: () => "view the full section.",
      form: () => "view the full form.",
    });
  }

  let descriptionText;
  if (type === "shopifySection" && sectionName) {
    descriptionText = (
      <>
        This is a placeholder for your Shopify Section,{" "}
        <span className="font-semibold">{sectionName}</span>.{" "}
      </>
    );
  } else {
    descriptionText = (
      <>
        This is a placeholder for the{" "}
        <span className="font-semibold">{title}</span> component.{" "}
      </>
    );
  }

  return (
    <div className="bg-sky-soft flex w-full gap-2 rounded-2xl p-6">
      <div className="max-w-fit grow">
        <BsInfoCircle className="text-primary grow" size={20} />
      </div>
      <div className="text-primary text-base">
        {descriptionOverride ?? (
          <>
            {descriptionText}
            <span className="font-semibold">Publish</span> or{" "}
            <span className="font-semibold">open a preview page</span> to{" "}
            {detail}
          </>
        )}
      </div>
    </div>
  );
};

const ProductOptionsPlaceholder: React.FC<RenderComponentPlaceholderProps> = ({
  title,
}) => {
  const ProductOptionsPlaceholderItemInactive: React.FC = () => {
    return (
      <div className="@r-lg/replo-canvas:w-1/3 @r-lg/replo-canvas:h-[250px] border-border flex h-[200px] w-full flex-col justify-center gap-4 rounded-[20px] border p-4">
        <div className="bg-surface relative w-full grow rounded-2xl">
          <div className="border-border absolute left-4 top-4 flex size-5 items-center justify-center rounded-full border-2">
            <div className="bg-border size-3 rounded-full"></div>
          </div>
        </div>
        <div className="flex w-full flex-col gap-3">
          <div className="bg-surface h-4 w-full rounded-full"></div>
          <div className="bg-surface h-4 w-10/12 rounded-full"></div>
        </div>
      </div>
    );
  };

  const ProductOptionsPlaceholderItemActive: React.FC = () => {
    return (
      <div className="@r-lg/replo-canvas:w-1/3 @r-lg/replo-canvas:h-[250px] border-border flex h-[200px] w-full flex-col justify-center gap-4 rounded-[20px] border-2 p-4">
        <div className="bg-border relative w-full grow rounded-2xl">
          <div className="border-muted absolute left-4 top-4 flex size-5 items-center justify-center rounded-full border-2">
            <div className="bg-muted size-3 rounded-full"></div>
          </div>
        </div>
        <div className="flex w-full flex-col gap-3">
          <div className="bg-border h-4 w-full rounded-full"></div>
          <div className="bg-border h-4 w-10/12 rounded-full"></div>
        </div>
      </div>
    );
  };
  return (
    <div className="@r-lg/replo-canvas:p-6 flex w-full flex-col gap-4 p-4">
      <div className="@r-md/replo-canvas:flex-row flex w-full flex-col items-center justify-center gap-4">
        <ProductOptionsPlaceholderItemInactive />
        <ProductOptionsPlaceholderItemActive />
        <ProductOptionsPlaceholderItemInactive />
      </div>
      <PlaceholderAlert title={title} type="productOptions" />
    </div>
  );
};

const ReviewStarsPlaceholder: React.FC<RenderComponentPlaceholderProps> = ({
  title,
}) => {
  return (
    <div className="flex flex-col items-start gap-2">
      <div className="text-muted flex items-center justify-start gap-2 rounded-2xl">
        <div className="@r-md/replo-canvas:text-5xl text-3xl font-bold">
          4.9
        </div>
        <div className="@r-md/replo-canvas:hidden flex gap-1">
          <BsStarFill size={14} />
          <BsStarFill size={14} />
          <BsStarFill size={14} />
          <BsStarFill size={14} />
          <BsStarHalf size={14} />
        </div>
        <div className="@r-md/replo-canvas:flex hidden gap-1">
          <BsStarFill size={16} />
          <BsStarFill size={16} />
          <BsStarFill size={16} />
          <BsStarFill size={16} />
          <BsStarHalf size={16} />
        </div>
      </div>
      <div className="bg-sky-soft flex h-fit max-w-sm gap-2 rounded-lg p-2">
        <div className="max-w-fit grow">
          <BsInfoCircle className="text-primary grow" size={18} />
        </div>
        <div className="text-primary text-base">
          <span className="font-semibold">{title}</span> placeholder
        </div>
      </div>
    </div>
  );
};

const ReviewFullPlaceholder: React.FC<RenderComponentPlaceholderProps> = ({
  title,
}) => {
  return (
    <div className="@r-lg/replo-canvas:p-6 flex w-full flex-col gap-4 p-4">
      <div className="bg-surface text-muted @r-md/replo-canvas:p-6 flex w-full flex-col items-center justify-center gap-4 rounded-2xl p-4">
        {/* Top Row */}
        <div className="flex w-full items-center justify-between">
          <div className="text-2xl font-bold uppercase">Reviews</div>
          <div className="flex items-center gap-2">
            <div className="@r-md/replo-canvas:text-5xl text-3xl font-bold">
              4.9
            </div>
            <div className="flex flex-col gap-1">
              <div className="@r-md/replo-canvas:hidden flex gap-1">
                <BsStarFill size={12} />
                <BsStarFill size={12} />
                <BsStarFill size={12} />
                <BsStarFill size={12} />
                <BsStarHalf size={12} />
              </div>
              <div className="@r-md/replo-canvas:flex hidden gap-1">
                <BsStarFill size={14} />
                <BsStarFill size={14} />
                <BsStarFill size={14} />
                <BsStarFill size={14} />
                <BsStarHalf size={14} />
              </div>
              <span className="text-sm font-semibold">50 reviews</span>
            </div>
          </div>
        </div>
        {/* Divider  */}
        <div className="bg-border h-[1px] w-full"></div>
        {/* Bottom Row */}
        <div className="flex w-full gap-4">
          <div className="@r-md/replo-canvas:w-[100px] @r-md/replo-canvas:h-[100px] bg-border h-[75px] w-[75px] rounded-2xl"></div>
          <div className="flex grow flex-col gap-4">
            <div className="flex gap-1">
              <BsStarFill size={14} />
              <BsStarFill size={14} />
              <BsStarFill size={14} />
              <BsStarFill size={14} />
              <BsStarHalf size={14} />
            </div>
            <div className="@r-md/replo-canvas:hidden bg-border h-3 w-1/2 rounded-full"></div>
            <div className="flex flex-col gap-2">
              <div className="@r-md/replo-canvas:h-4 bg-border h-3 w-full rounded-full"></div>
              <div className="@r-md/replo-canvas:h-4 bg-border h-3 w-10/12 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>

      <PlaceholderAlert title={title} type="reviewFull" />
    </div>
  );
};

const GalleryPlaceholder: React.FC<RenderComponentPlaceholderProps> = ({
  title,
}) => {
  return (
    <div className="@r-lg/replo-canvas:p-6 flex w-full flex-col gap-4 p-4">
      <div className="@r-sm/replo-canvas:p-6 bg-surface flex w-full flex-col gap-4 rounded-2xl p-4">
        <div className="@r-md/replo-canvas:justify-between flex w-full items-center justify-end">
          <div className="@r-md/replo-canvas:block bg-neutral-200 hidden h-[14px] w-5/12 rounded-full"></div>
          <div className="@r-md/replo-canvas:w-4/12 flex w-1/2 items-center justify-center gap-4">
            <div className="bg-neutral-200 h-[14px] grow rounded-full"></div>
            <div className="border-border rounded-full border bg-white p-3">
              <BsChevronRight
                style={{ strokeWidth: 2 }}
                className="text-muted"
                size={14}
              />
            </div>
          </div>
        </div>
        <div className="@r-md/replo-canvas:flex-row flex w-full flex-col gap-4">
          <div className="bg-neutral-200 flex h-[200px] w-full items-center justify-center rounded-2xl">
            <BsImage className="text-muted" size={40} />
          </div>
          <div className="bg-neutral-200 flex h-[200px] w-full items-center justify-center rounded-2xl">
            <BsImage className="text-muted" size={40} />
          </div>
          <div className="bg-neutral-200 flex h-[200px] w-full items-center justify-center rounded-2xl">
            <BsImage className="text-muted" size={40} />
          </div>
        </div>
      </div>
      <PlaceholderAlert title={title} type="gallery" />
    </div>
  );
};

const ShopifySectionPlaceholder: React.FC<RenderComponentPlaceholderProps> = ({
  title,
  sectionName,
}) => {
  return (
    <div className="@r-lg/replo-canvas:p-6 mx-auto flex w-full max-w-2xl flex-col gap-4 p-4">
      <div className="@r-sm/replo-canvas:p-6 bg-surface flex w-full flex-col gap-4 rounded-2xl p-4">
        <div className="flex w-full flex-col items-center gap-2">
          <div className="bg-neutral-200 h-[20px] w-8/12 rounded-full"></div>
          <div className="bg-neutral-200 h-[14px] w-6/12 rounded-full"></div>
        </div>
        <div className="flex w-full flex-col gap-4">
          <div className="@r-md/replo-canvas:flex-row flex w-full flex-col items-center justify-center gap-4">
            <div className="@r-sm/replo-canvas:w-1/2 @r-md/replo-canvas:w-1/3 bg-neutral-200 flex h-[120px] w-full items-center justify-center rounded-2xl"></div>
            <div className="@r-sm/replo-canvas:w-1/2 @r-md/replo-canvas:w-1/3 bg-neutral-200 flex h-[120px] w-full items-center justify-center rounded-2xl"></div>
            <div className="@r-md/replo-canvas:flex bg-neutral-200 hidden h-[120px] w-1/3 items-center justify-center rounded-2xl"></div>
          </div>
          <div className="@r-md/replo-canvas:h-12 bg-neutral-200 flex h-8 w-full items-center justify-center rounded-2xl"></div>
        </div>
      </div>
      <PlaceholderAlert
        title={title}
        type="shopifySection"
        sectionName={sectionName}
      />
    </div>
  );
};

const HTMLPlaceholder: React.FC<RenderComponentPlaceholderProps> = ({
  title,
}) => {
  return (
    <div className="mx-auto my-2 w-full max-w-2xl">
      <PlaceholderAlert title={title} type="html" />
    </div>
  );
};

const FormPlaceholder: React.FC<RenderComponentPlaceholderProps> = ({
  title,
}) => {
  const FormPlaceholderDesktop: React.FC = () => {
    return (
      <div className="@r-md/replo-canvas:flex bg-surface border-border hidden w-full flex-col gap-4 rounded-2xl border p-10">
        <div className="flex w-full gap-4">
          <div className="bg-neutral-200 h-[50px] w-full rounded-2xl"></div>
          <div className="bg-neutral-200 h-[50px] w-full rounded-2xl"></div>
        </div>
        <div className="bg-neutral-200 h-[120px] w-full rounded-2xl"></div>
        <div className="bg-muted ml-auto flex h-[48px] w-[150px] items-center justify-center rounded-lg text-2xl font-semibold text-white">
          Submit
        </div>
      </div>
    );
  };

  const FormPlaceholderMobile: React.FC = () => {
    return (
      <div className="@r-md/replo-canvas:hidden @r-sm/replo-canvas:p-6 bg-surface border-border flex w-full flex-col gap-4 rounded-2xl border p-4">
        <div className="flex w-full flex-col gap-2">
          <div className="bg-neutral-200 h-[12px] w-[36%] rounded-full"></div>
          <div className="bg-neutral-200 h-[40px] w-full rounded-xl"></div>
        </div>
        <div className="flex w-full flex-col gap-2">
          <div className="bg-neutral-200 h-[12px] w-[30%] rounded-full"></div>
          <div className="bg-neutral-200 h-[40px] w-full rounded-xl"></div>
        </div>
        <div className="flex w-full flex-col gap-2">
          <div className="bg-neutral-200 h-[12px] w-[42%] rounded-full"></div>
          <div className="bg-neutral-200 h-[150px] w-full rounded-xl"></div>
        </div>
        <div className="bg-muted ml-auto flex h-[48px] w-full items-center justify-center rounded-lg text-2xl font-semibold text-white">
          Submit
        </div>
      </div>
    );
  };

  return (
    <div className="@r-lg/replo-canvas:p-6 flex w-full flex-col gap-4 p-4">
      <FormPlaceholderDesktop />
      <FormPlaceholderMobile />
      <PlaceholderAlert title={title} />
    </div>
  );
};

const RenderComponentPlaceholder: React.FC<RenderComponentPlaceholderProps> = (
  props,
) => {
  const { type, ...restProps } = props;

  switch (type) {
    case "productOptions":
      return <ProductOptionsPlaceholder {...restProps} />;
    case "reviewStars":
      return <ReviewStarsPlaceholder {...restProps} />;
    case "reviewFull":
      return <ReviewFullPlaceholder {...restProps} />;
    case "gallery":
      return <GalleryPlaceholder {...restProps} />;
    case "form":
      return <FormPlaceholder {...restProps} />;
    case "shopifySection":
      return <ShopifySectionPlaceholder {...restProps} />;
    case "html":
      return <HTMLPlaceholder {...restProps} />;
    default:
      return <PlaceholderAlert {...restProps} />;
  }
};

export default RenderComponentPlaceholder;
